import { nanoid } from 'nanoid';

import { QUESTION_TYPES } from '@api/instant_survey';

import { toast } from '@utilities';

import { DEFAULT_QUESTION_GROUP_NAME } from '../../../utilities/constants';
import { isQuestionGroupNameTaken } from '../../../utilities/helpers';

/**
 * Cleans up option logic references to a specific question/answer combination
 * @param {Object} modifiedQuestionLogic - The question logic object to modify
 * @param {string|number} targetQuestionId - The question ID to search for
 * @param {Set} deletedAnswerIds - Set of answer IDs that were deleted (optional)
 * @returns {boolean} Whether any changes were made
 */
const cleanupOptionLogicReferences = (
  modifiedQuestionLogic,
  targetQuestionId,
  deletedAnswerIds = null
) => {
  let hasChanges = false;

  Object.keys(modifiedQuestionLogic).forEach((questionKey) => {
    if (!modifiedQuestionLogic[questionKey].option) return;

    Object.keys(modifiedQuestionLogic[questionKey].option).forEach((answerKey) => {
      const optionLogicForAnswer = modifiedQuestionLogic[questionKey].option[answerKey];

      if (!optionLogicForAnswer.logic) return;

      const originalLength = optionLogicForAnswer.logic.length;

      // Filter logic rows based on whether we're removing entire question or specific answers
      optionLogicForAnswer.logic = optionLogicForAnswer.logic.filter((logicRow) => {
        // Keep logic rows that reference a different question
        if (String(logicRow.question?.id) !== String(targetQuestionId)) return true;

        // If we have deleted answer IDs, filter them out from the answers array.
        // Numeric/quota-group rows have no `answers` array — they aren't
        // answer-based, so deleting specific answers must not touch them.
        if (deletedAnswerIds) {
          if (!Array.isArray(logicRow.answers)) return true;

          logicRow.answers = logicRow.answers.filter((ans) => !deletedAnswerIds.has(ans.id));
          return logicRow.answers.length > 0;
        }
        return false;
      });

      if (optionLogicForAnswer.logic.length !== originalLength) {
        hasChanges = true;
      }

      if (optionLogicForAnswer.logic.length === 0) {
        delete modifiedQuestionLogic[questionKey].option[answerKey];
      }
    });

    if (Object.keys(modifiedQuestionLogic[questionKey].option).length === 0) {
      delete modifiedQuestionLogic[questionKey].option;
    }
  });

  return hasChanges;
};

/**
 * Creates an independent local copy of a sequential monadic sub-question.
 * @param {Object} subQuestion
 * @returns {Object}
 */
export const duplicateSubQuestion = ({
  _delete,
  answers = [],
  id,
  noneAnswer,
  subQuestions = [],
  translations,
  ...subQuestion
}) => ({
  ...subQuestion,
  answers: answers
    .filter(({ _delete: isDeleted }) => !isDeleted)
    .map(({ _delete, id, translations, ...answer }) => ({
      ...answer,
      id: nanoid(),
      translations: translations
        ?.filter(({ _delete: isDeleted }) => !isDeleted)
        .map(({ _delete, id, ...translation }) => ({
          ...translation,
          id: nanoid(),
        })),
    })),
  id: nanoid(),
  noneAnswer: noneAnswer
    ? {
        ...noneAnswer,
        id: nanoid(),
        translations: noneAnswer.translations?.map(({ id, ...translation }) => ({
          ...translation,
          id: nanoid(),
        })),
      }
    : noneAnswer,
  subQuestions: subQuestions
    .filter(({ _delete: isDeleted }) => !isDeleted)
    .map(({ _delete, id, translations, ...subQuestion }) => ({
      ...subQuestion,
      id: nanoid(),
      translations: translations
        ?.filter(({ _delete: isDeleted }) => !isDeleted)
        .map(({ _delete, id, ...translation }) => ({
          ...translation,
          id: nanoid(),
        })),
    })),
  translations: translations?.map(({ id, ...translation }) => ({
    ...translation,
    id: nanoid(),
  })),
});

/**
 * Checks to see if any question logic needs to be changed
 * @param {Object} options.originalQuestion
 * @param {Object} options.question
 * @param {Object} options.questionLogic
 * @param {Object[]} options.questions
 */
export const cleanupLogicAfterEdit = ({ originalQuestion, question, questionLogic, questions }) => {
  let hasLogicChanges = false;
  const modifiedQuestionLogic = { ...questionLogic };

  // recommended question became a custom question, treat it as a removal
  if (originalQuestion.id !== question.id)
    return cleanupLogicAfterRemoval({
      question: originalQuestion,
      questionId: originalQuestion.id,
      questionLogic,
      questions,
    });

  const disqualifyLogic = modifiedQuestionLogic[question.id]?.disqualify;

  if (disqualifyLogic) {
    if (
      [QUESTION_TYPES.ESSAY.value, QUESTION_TYPES['RANK-DRAGDROP'].value].includes(question.type)
    ) {
      // cannot disqualify if the question type is essay or rank
      delete modifiedQuestionLogic[question.id].disqualify;
      hasLogicChanges = true;
    } else if (
      Array.isArray(disqualifyLogic.logic)
        ? disqualifyLogic.logic.some((row) => (row.answers || row.values || []).length > 0)
        : (disqualifyLogic.answers || []).length > 0
    ) {
      // clear answers/values to prevent prior selections from 'sticking'

      if (shouldClearQuestionLogic(question, originalQuestion)) {
        if (Array.isArray(disqualifyLogic.logic)) {
          modifiedQuestionLogic[question.id].disqualify.logic = modifiedQuestionLogic[
            question.id
          ].disqualify.logic.map((row) => ({
            ...row,
            answers: row.answers !== undefined ? [] : undefined,
            values: row.values !== undefined ? [] : undefined,
          }));
        } else {
          modifiedQuestionLogic[question.id].disqualify.answers = [];
        }
        hasLogicChanges = true;
      }
    }
  }

  Object.keys(modifiedQuestionLogic).forEach((id) => {
    const skipLogic = modifiedQuestionLogic[id].skip?.logic;
    if (skipLogic) {
      skipLogic.forEach((row) => {
        if (row.question?.id === question.id) {
          if (!shouldClearQuestionLogic(question, row.question)) {
            return;
          }

          row.answers = [];
          row.question = [
            QUESTION_TYPES.ESSAY.value,
            QUESTION_TYPES.NUMERIC.value,
            QUESTION_TYPES['RANK-DRAGDROP'].value,
          ].includes(question.type)
            ? undefined
            : question;
          hasLogicChanges = true;
        }
      });
    }
  });

  // Clean up option logic when answers are deleted
  const deletedAnswerIds = new Set(
    originalQuestion.answers
      .filter((origAnswer) => !question.answers.some((ans) => ans.id === origAnswer.id))
      .map((ans) => ans.id)
  );

  if (deletedAnswerIds.size > 0) {
    const optionLogicChanged = cleanupOptionLogicReferences(
      modifiedQuestionLogic,
      question.id,
      deletedAnswerIds
    );
    hasLogicChanges = hasLogicChanges || optionLogicChanged;
  }

  if (hasLogicChanges) displayQuestionLogicUpdatedToast();
  return modifiedQuestionLogic;
};

const shouldClearQuestionLogic = (question, originalQuestion) => {
  if (question.type !== originalQuestion.type) {
    return true;
  }

  // Check if any selectable options were removed/edited
  return originalQuestion.answers.some(({ id: originalId, value: originalValue }) => {
    const matchingNewOption = question.answers.find(
      ({ id, value }) => originalId === id && originalValue === value
    );
    return !matchingNewOption;
  });
};

/**
 * Checks to see if any question logic needs to be changed
 * @param {Object} options.question
 * @param {Number} options.questionId
 * @param {Object} options.questionLogic
 * @param {Object[]} options.questions
 * @param {Boolean} [options.shouldDisplayToast]
 */
export const cleanupLogicAfterRemoval = ({
  question,
  questionId,
  questionLogic,
  questions,
  shouldDisplayToast = true,
}) => {
  let hasLogicChanges = false;
  const modifiedQuestionLogic = { ...questionLogic };
  const questionIdsToRemove = new Set([
    String(questionId),
    ...(question?.type === QUESTION_TYPES.SEQUENTIAL_MONADIC.value
      ? (question.subQuestions || []).map(({ id }) => String(id))
      : []),
  ]);

  // remove logic for question that was removed
  questionIdsToRemove.forEach((idToRemove) => {
    if (modifiedQuestionLogic[idToRemove]?.disqualify || modifiedQuestionLogic[idToRemove]?.skip) {
      delete modifiedQuestionLogic[idToRemove];
      hasLogicChanges = true;
    }
    // remove option logic for question that was removed
    if (modifiedQuestionLogic[idToRemove]?.option) {
      delete modifiedQuestionLogic[idToRemove].option;
      hasLogicChanges = true;
    }
  });

  Object.keys(modifiedQuestionLogic).forEach((id) => {
    const skipLogic = modifiedQuestionLogic[id].skip?.logic;
    if (skipLogic) {
      // if the removal of a question moved this question to the first postion, remove skip logic
      if (questions.findIndex((question) => String(id) === String(question.id)) === 0) {
        delete modifiedQuestionLogic[id]?.skip;
        hasLogicChanges = true;
        return;
      }

      // remove any skip logic that references the removed question
      // Check if the question has an id, as there are some cases the logic is for quota groups not questions
      const updatedSkipLogic = skipLogic.filter(
        (row) => !questionIdsToRemove.has(String(row.question?.id))
      );
      if (updatedSkipLogic.length !== skipLogic.length) {
        if (updatedSkipLogic.length === 0) {
          delete modifiedQuestionLogic[id].skip;
        } else {
          modifiedQuestionLogic[id].skip.logic = updatedSkipLogic;
        }
        hasLogicChanges = true;
      }
    }
  });

  // Clean up option logic that references the removed question(s)
  questionIdsToRemove.forEach((idToRemove) => {
    const optionLogicChanged = cleanupOptionLogicReferences(modifiedQuestionLogic, idToRemove);
    hasLogicChanges = hasLogicChanges || optionLogicChanged;
  });

  if (hasLogicChanges && shouldDisplayToast) displayQuestionLogicUpdatedToast();
  return modifiedQuestionLogic;
};

/**
 * Shows a toast letting users know question logic was changed after a questionnaire edit.
 * @returns {void}
 */
export const displayQuestionLogicUpdatedToast = () => {
  toast(
    <p>
      Some logic in this questionnaire has been updated or removed due to a survey question change.
    </p>
  );
};

/**
 * Updates piping mentions while preserving the two numbering systems:
 * - canonical (data-mention-id) for Preview resolution
 * - display ({answer from qX}) for Builder text
 * @param {Object} params
 * @param {String} params.value
 * @param {Number} params.oldCanonicalQuestionNumber
 * @param {Number} params.newCanonicalQuestionNumber
 * @param {Number} params.oldDisplayQuestionNumber
 * @param {Number} params.newDisplayQuestionNumber
 * @returns {String}
 */
export const replacePipingMentionReference = ({
  newCanonicalQuestionNumber,
  newDisplayQuestionNumber,
  oldCanonicalQuestionNumber,
  oldDisplayQuestionNumber,
  value,
}) => {
  if (typeof value !== 'string') {
    return value;
  }

  let nextValue = value;

  if (Number.isFinite(oldCanonicalQuestionNumber) && Number.isFinite(newCanonicalQuestionNumber)) {
    nextValue = nextValue.replace(
      // eslint-disable-next-line security/detect-unsafe-regex
      /(data-mention-id="answer from q)(\d+)(\.\d+)?(")/g,
      (match, prefix, questionNumber, subQuestionSuffix = '', suffix) =>
        Number(questionNumber) === Number(oldCanonicalQuestionNumber)
          ? `${prefix}${newCanonicalQuestionNumber}${subQuestionSuffix}${suffix}`
          : match
    );
  }

  if (Number.isFinite(oldDisplayQuestionNumber) && Number.isFinite(newDisplayQuestionNumber)) {
    nextValue = nextValue.replace(
      // eslint-disable-next-line security/detect-unsafe-regex
      /\{answer from q(\d+)(\.\d+)?\}/g,
      (match, questionNumber, subQuestionSuffix = '') =>
        Number(questionNumber) === Number(oldDisplayQuestionNumber)
          ? `{answer from q${newDisplayQuestionNumber}${subQuestionSuffix}}`
          : match
    );
  }

  return nextValue;
};

/**
 * Remaps question logic for a set of copied questions onto their newly-created counterparts.
 * Mirrors the backend copy_question_logic: entries are re-keyed to the new question id and every
 * answer/question reference to a copied entity is swapped for its copy. References to entities that
 * were not copied are left pointing at the original.
 * @param {Object} options
 * @param {Map<number, Object>} options.answerMap old answer id -> new answer object
 * @param {Object} options.questionLogic in-memory question logic (post-transformQuestionLogic)
 * @param {Map<number, Object>} options.questionMap old question id -> new question object
 * @returns {Object} new logic entries keyed by the new question id
 */
export const remapCopiedQuestionLogic = ({ answerMap, questionLogic, questionMap }) => {
  const result = {};

  const remapAnswers = (answers = []) =>
    answers.map((answer) => answerMap.get(answer.id)).filter(Boolean);

  // Disqualify rows target the question's own answers (always copied); url_variable rows carry none.
  const remapDisqualifyRow = (row) =>
    row.url_variable ? row : { ...row, answers: remapAnswers(row.answers) };

  // Skip / option rows reference another question. Only remap when that question was also copied;
  // otherwise leave the row pointing at the original question and its answers.
  const remapReferenceRow = (row) => {
    if (row.url_variable) return row;
    if (!row.question || !questionMap.has(row.question.id)) return row;

    const newQuestion = questionMap.get(row.question.id);
    return Object.hasOwn(row, 'comparison_value')
      ? { ...row, question: newQuestion }
      : { ...row, answers: remapAnswers(row.answers), question: newQuestion };
  };

  for (const [oldQuestionId, newQuestion] of questionMap) {
    const logic = questionLogic?.[oldQuestionId];
    if (!logic) continue;

    const newEntry = {};

    if (logic.disqualify) {
      const { disqualify } = logic;
      newEntry.disqualify = Array.isArray(disqualify.logic)
        ? { ...disqualify, logic: disqualify.logic.map(remapDisqualifyRow) }
        : { ...disqualify, answers: remapAnswers(disqualify.answers) };
    }

    if (logic.skip) {
      newEntry.skip = { ...logic.skip, logic: logic.skip.logic.map(remapReferenceRow) };
    }

    if (logic.option) {
      const newOption = {};
      for (const [answerId, optionLogic] of Object.entries(logic.option)) {
        const newAnswer = answerMap.get(Number(answerId));
        if (!newAnswer) continue;
        newOption[newAnswer.id] = {
          ...optionLogic,
          logic: optionLogic.logic.map(remapReferenceRow),
        };
      }
      if (Object.keys(newOption).length > 0) newEntry.option = newOption;
    }

    result[newQuestion.id] = newEntry;
  }

  return result;
};

/**
 * Remaps carry-forward entries owned by copied questions. Sources copied with the group point to
 * their copies; sources outside the group continue pointing to the original question.
 * @param {Object} options
 * @param {Object} [options.carryForwardLogic]
 * @param {Map<number, Object>} options.questionMap old question id -> new question object
 * @returns {Object} new carry-forward entries keyed by the copied target question id
 */
export const remapCopiedCarryForwardLogic = ({ carryForwardLogic = {}, questionMap }) => {
  const result = {};

  for (const [oldQuestionId, newQuestion] of questionMap) {
    const logic = carryForwardLogic?.[oldQuestionId];
    if (!logic) continue;

    result[newQuestion.id] = {
      ...logic,
      sourceSurveyQuestionId:
        questionMap.get(logic.sourceSurveyQuestionId)?.id ?? logic.sourceSurveyQuestionId,
    };
  }

  return result;
};

/**
 * Duplicates a question group: creates a new backend group, creates a fresh copy of each member
 * question (real ids), and remaps the group's question logic onto those copies.
 * @param {Object} options
 * @param {Object} [options.carryForwardLogic]
 * @param {Function} options.createQuestion async ({ question }) => createdQuestion
 * @param {Function} options.createQuestionGroup async ({ name, parentGroup, randomizeOrder }) => group
 * @param {Object} options.group the source QuestionGroup
 * @param {Object} options.questionGroups all question groups (for name dedup)
 * @param {Object} options.questionLogic current in-memory question logic
 * @param {Object[]} options.questions all survey questions
 * @returns {Promise<{
 *   createdQuestions: Object[],
 *   newGroup: Object,
 *   remappedCarryForwardLogic: Object,
 *   remappedLogic: Object,
 * }>}
 */
export const buildCopiedGroup = async ({
  carryForwardLogic,
  createQuestion,
  createQuestionGroup,
  group,
  questionGroups,
  questionLogic,
  questions,
}) => {
  const members = questions.filter(
    (question) => question.questionGroup === group.id && !question._delete
  );
  if (members.length === 0) {
    return {
      createdQuestions: [],
      newGroup: null,
      remappedCarryForwardLogic: {},
      remappedLogic: {},
    };
  }

  const trimmedGroupName = (group.name || DEFAULT_QUESTION_GROUP_NAME).trim();
  let copiedGroupName = `${trimmedGroupName} (copy)`;
  let copiedGroupNameSuffix = 2;
  while (isQuestionGroupNameTaken({ name: copiedGroupName, questionGroups })) {
    copiedGroupName = `${trimmedGroupName} (copy ${copiedGroupNameSuffix})`;
    copiedGroupNameSuffix += 1;
  }

  const newGroup = await createQuestionGroup({
    name: copiedGroupName,
    ...(group.parentGroup != null ? { parentGroup: group.parentGroup } : {}),
    randomizeOrder: group.randomizeOrder ?? false,
  });

  const createdQuestions = [];
  for (const member of members) {
    const { questionClientId: _clientId, questionGroup: _group, ...rest } = member;
    const created = await createQuestion({ question: stripQuestionIds(rest) });
    createdQuestions.push({ ...created, questionGroup: newGroup.id });
  }

  // Pair each source question and answer with its newly created counterpart so copied logic can
  // reference the new backend IDs. Sequential-monadic sub-questions are mapped recursively.
  const answerMap = new Map();
  const questionMap = new Map();
  const mapCopiedIds = (source, created) => {
    if (!source || !created) return;

    questionMap.set(source.id, created);
    (source.answers || []).forEach((answer, index) => {
      const createdAnswer = created.answers?.[index];
      if (createdAnswer) answerMap.set(answer.id, createdAnswer);
    });
    (source.subQuestions || []).forEach((subQuestion, index) =>
      mapCopiedIds(subQuestion, created.subQuestions?.[index])
    );
  };
  members.forEach((sourceQuestion, index) => mapCopiedIds(sourceQuestion, createdQuestions[index]));

  const remappedCarryForwardLogic = remapCopiedCarryForwardLogic({
    carryForwardLogic,
    questionMap,
  });
  const remappedLogic = remapCopiedQuestionLogic({ answerMap, questionLogic, questionMap });

  return { createdQuestions, newGroup, remappedCarryForwardLogic, remappedLogic };
};

/**
 * Rebuilds a carry-forward destination question's answers so that its
 * carry-forward-generated options stay in sync with the (just edited) source
 * question's options.
 *
 *
 * @param {Object} options
 * @param {import('@api/instant_survey/types').Question} options.destinationQuestion - a question carrying forward from the source
 * @param {import('@api/instant_survey/types').Question} options.sourceQuestion - the just-edited source question
 * @returns {import('@api/instant_survey/types').Question} the destination question with synced answers
 */
export const syncCarryForwardAnswers = ({ destinationQuestion, sourceQuestion }) => {
  const eligibleSourceOptions = sourceQuestion.answers.filter(
    ({ _delete, isNoneOfTheAbove }) => !_delete && !isNoneOfTheAbove
  );
  const eligibleSourceIds = new Set(eligibleSourceOptions.map(({ id }) => id));

  const existingCarryForwardAnswers = destinationQuestion.answers.filter(
    ({ isCarryForwardGenerated }) => isCarryForwardGenerated
  );
  const ownAnswers = destinationQuestion.answers.filter(
    ({ isCarryForwardGenerated }) => !isCarryForwardGenerated
  );
  const carryForwardAnswerBySourceId = new Map(
    existingCarryForwardAnswers.map((answer) => [answer.carriedFromOptionId, answer])
  );

  // Refresh existing carry-forward answers and append new ones, following the
  // current source option order.
  const syncedCarryForwardAnswers = eligibleSourceOptions.map(
    ({ id: sourceOptionId, isAnchored, isExclusive, ...sourceOption }) => {
      const existingAnswer = carryForwardAnswerBySourceId.get(sourceOptionId);
      return {
        ...sourceOption,
        carriedFromOptionId: sourceOptionId,
        id: existingAnswer?.id ?? nanoid(),
        isCarryForwardGenerated: true,
      };
    }
  );

  // Soft-delete carry-forward answers whose source option no longer exists so
  // the backend removes them (client-only answers can simply be dropped).
  const deletedCarryForwardAnswers = existingCarryForwardAnswers
    .filter(
      (answer) =>
        !eligibleSourceIds.has(answer.carriedFromOptionId) && typeof answer.id === 'number'
    )
    .map((answer) => ({ ...answer, _delete: true }));

  return {
    ...destinationQuestion,
    answers: [...syncedCarryForwardAnswers, ...ownAnswers, ...deletedCarryForwardAnswers],
  };
};

export const stripQuestionIds = (question) => {
  const { answers = [], id: _, subQuestions = [], translations = [], ...rest } = question;

  return {
    ...rest,
    answers: answers.map(({ id: _, translations = [], ...answer }) => ({
      ...answer,
      translations: translations.map(({ id: _, ...translation }) => translation),
    })),
    subQuestions: subQuestions.map(stripQuestionIds),
    translations: translations.map(({ id: _, ...translation }) => translation),
  };
};

export const getPipingQuestionNumbersById = (questionsList = []) => {
  let questionNumber = 1;

  return questionsList.reduce((acc, question) => {
    if (question?._delete) {
      return acc;
    }

    acc[question.id] = questionNumber;
    questionNumber += 1;
    return acc;
  }, {});
};

export const replacePipingMentionReferences = ({ mappings = [], value }) => {
  if (typeof value !== 'string' || mappings.length === 0) {
    return value;
  }

  const canonicalQuestionNumberMap = new Map();

  const getRangeStart = (questionNumber) => String(questionNumber).split('-')[0];

  const displayQuestionNumberMap = new Map(
    mappings
      .filter(
        ({ newDisplayQuestionNumber, oldDisplayQuestionNumber }) =>
          oldDisplayQuestionNumber !== undefined &&
          oldDisplayQuestionNumber !== null &&
          newDisplayQuestionNumber !== undefined &&
          newDisplayQuestionNumber !== null
      )
      .map(({ newDisplayQuestionNumber, oldDisplayQuestionNumber }) => [
        getRangeStart(oldDisplayQuestionNumber),
        getRangeStart(newDisplayQuestionNumber),
      ])
  );

  mappings.forEach(({ newCanonicalQuestionNumber, oldCanonicalQuestionNumber }) => {
    if (
      Number.isFinite(oldCanonicalQuestionNumber) &&
      Number.isFinite(newCanonicalQuestionNumber)
    ) {
      canonicalQuestionNumberMap.set(
        Number(oldCanonicalQuestionNumber),
        Number(newCanonicalQuestionNumber)
      );
    }
  });

  let nextValue = value;

  if (canonicalQuestionNumberMap.size > 0) {
    nextValue = nextValue.replace(
      // eslint-disable-next-line security/detect-unsafe-regex
      /(data-mention-id="answer from q)(\d+)(\.\d+)?(")/g,
      (match, prefix, questionNumber, subQuestionSuffix = '', suffix) => {
        const mappedQuestionNumber = canonicalQuestionNumberMap.get(Number(questionNumber));

        return Number.isFinite(mappedQuestionNumber)
          ? `${prefix}${mappedQuestionNumber}${subQuestionSuffix}${suffix}`
          : match;
      }
    );
  }

  if (displayQuestionNumberMap.size > 0) {
    nextValue = nextValue.replace(
      // eslint-disable-next-line security/detect-unsafe-regex
      /\{answer from q(\d+)(?:-(\d+))?(\.\d+)?\}/g,
      (match, startQuestionNumber, endQuestionNumber, subQuestionSuffix = '') => {
        const mappedStartQuestionNumber = displayQuestionNumberMap.get(String(startQuestionNumber));

        if (!mappedStartQuestionNumber) {
          return match;
        }

        const mappedRange = endQuestionNumber
          ? `-${
              Number(endQuestionNumber) +
              (Number(mappedStartQuestionNumber) - Number(startQuestionNumber))
            }`
          : '';

        return `{answer from q${mappedStartQuestionNumber}${mappedRange}${subQuestionSuffix}}`;
      }
    );
  }

  return nextValue;
};
